<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { wsClient } from '@/net/WebSocketClient'
import { MsgIDs } from '@/defines/MsgIDs'
import { hall } from '@/hall'
import HallTop from '@/components/HallTop.vue'
import GameCategorySection from '@/components/GameCategorySection.vue'
import SingleCategoryGameList from '@/components/SingleCategoryGameList.vue'
import ProviderListSection from '@/components/ProviderListSection.vue'
import ProviderGameListPopup from '@/components/ProviderGameListPopup.vue'
import {
  Client2ProxyAckActivityDetail,
  Client2ProxyReqActivityDetail,
  Client2ProxyReqLogin,
  ClientActivityDataPush,
} from '@/proto/proto'

import { GameType, Providers } from '@/defines/EnumDefine'
import defaultGameIcon from '@/assets/hall/game_icon_default.webp'
import QuickActionActivities from '@/components/QuickActionActivities.vue'
import AllActivitiesPopup from '@/components/AllActivitiesPopup.vue'
import HallBanner from '@/components/HallBanner.vue'
import DepositPanel from '@/components/DepositPanel.vue'
import SidebarMenu from '@/components/SidebarMenu.vue'
import AccountPanel from '@/components/AccountPanel.vue'
import GiftSelection from '@/components/pinduo/GiftSelection.vue'
import PinDuoPanel from '@/components/pinduo/PinDuoPanel.vue'
import { ServerDefine } from '@/config/ServerDefine'

const activeTab = ref('Casino')
const showSidebar = ref(false)

const hasClaimedToday = ref(localStorage.getItem('pinduo_claimed') === new Date().toDateString())
const showPinduo = ref(false)

const handleGiftPick = () => {
  hasClaimedToday.value = true
  localStorage.setItem('pinduo_claimed', new Date().toDateString())
}

const handleSidebarNavigate = (id: string) => {
  if (id === 'Deposit') {
    activeTab.value = 'Deposit'
  }
  // Handle other navigation ids if needed
}

const searchQuery = ref('')
const activeCategory = ref<GameType>(GameType.All)
interface Game {
  id: number
  name: string
  icon: string
  isTop: boolean
  isBuy?: boolean
  bg: string
}

const showSearchResults = ref(false)
const searchResults = ref<Game[]>([])
const displayCount = ref(12)

const showMoreActionsPopup = ref(false)
const showProviderPopup = ref(false)
const selectedProvider = ref<Providers>(Providers.All)

const handleActivityClick = (act: Activity) => {
  console.log('Clicked activity:', act)
  // Implement activity redirect
}

const handleOpenProvider = (providerId: Providers) => {
  selectedProvider.value = providerId
  showProviderPopup.value = true
}

interface Activity {
  type: number
  label: string
  icon: string
  badge?: boolean
}

const quickActions = ref<Activity[]>([])

// Mapping from Activity Type ID to local icon/name
const ACTIVITY_MAP: Record<number, { name: string; icon: string }> = {
  1: { name: 'Bind Phone', icon: 'Registration' },
  2: { name: 'First Deposit', icon: 'FirstReward' },
  3: { name: 'Lucky Wheel', icon: 'LuckyWheel' },
  4: { name: 'Bonanza', icon: 'Bonanza' },
  5: { name: 'Daily Task', icon: 'DailyTask' },
  6: { name: 'VIP Privilege', icon: 'VIPPrivilege' },
  7: { name: 'Daily Cash', icon: 'DailyCash' },
  8: { name: 'Lucky Gacha', icon: 'LuckyGacha' },
  9: { name: 'Lucky Gems', icon: 'LuckyGems' },
  10: { name: 'Rank', icon: 'Rank' },
  11: { name: 'Mystica Box', icon: 'MysticaBox' },
  12: { name: 'Water Wheel', icon: 'WaterWheel' },
  13: { name: 'Free Spin', icon: 'FreeSpinEveryday' },
  14: { name: 'Vip Card', icon: 'VipCard' },
}

const handleActivityAck = (payload: Uint8Array) => {
  const rsp = Client2ProxyAckActivityDetail.decode(payload)
  console.log('[Net] Activity Detail Ack:', rsp)
  if (rsp.Activitys && Array.isArray(rsp.Activitys)) {
    quickActions.value = rsp.Activitys.filter(
      (a) => a.type !== null && a.type !== undefined && ACTIVITY_MAP[a.type as number],
    ).map((a) => {
      const config = ACTIVITY_MAP[a.type as number]!
      return {
        type: a.type as number,
        label: config.name,
        icon: config.icon,
        badge: true,
      }
    })
  }

  hall.eventDispatcher.emit(hall.EventDefine.ChangeBindActivityRed) //刷新红点
}

const handleActivityPush = (payload: Uint8Array) => {
  const push = ClientActivityDataPush.decode(payload)
  console.log('[Net] Activity Data Push:', push)
  const req = Client2ProxyReqActivityDetail.create({ UserID: 0 })
  const data = Client2ProxyReqActivityDetail.encode(req).finish()
  wsClient.send(MsgIDs.Client2ProxyReqActivityDetail, data)
}

const displayedResults = computed(() => {
  return searchResults.value.slice(0, displayCount.value)
})

const handleSearch = () => {
  if (!searchQuery.value.trim()) {
    alert('Please enter a keywords to search')
    return
  }

  const searchStr = searchQuery.value.toLowerCase()

  hall.hallModule.cleanSrarch()
  hall.hallModule.postGameList(1, searchStr, () => {
    // 数据请求成功后，打开界面
    const searchGames = hall.hallModule.getSubgameIDList(0, 0, searchStr) || []
    const searchGameInfos = []
    for (let index = 0; index < searchGames.length; index++) {
      const gId = searchGames[index]
      const gameInfo = hall.hallModule.getSubgameInfo(Number(gId))
      gameInfo.icon = ServerDefine.GAME_ICON_URL + gId + '.png'
      searchGameInfos[index] = gameInfo
    }
    searchResults.value = searchGameInfos
    displayCount.value = searchGameInfos.length
    showSearchResults.value = true
  })
}

const handleLoadMore = () => {
  displayCount.value += 12
}

const clearSearchInput = () => {
  searchQuery.value = ''
  showSearchResults.value = false
  displayCount.value = 12
}

onMounted(() => {
  hall.init()
  hall.hallModule.onLoad()
  wsClient.on(MsgIDs.Client2ProxyAckActivityDetail, handleActivityAck)
  wsClient.on(MsgIDs.ClientActivityDataPush, handleActivityPush)

  wsClient.onConnect(() => {
    const loginReq = Client2ProxyReqLogin.create({
      Origin: 1,
      ClientMark: 'guest_web_client',
      platformType: 3,
      version: '1.0.0',
    })
    wsClient.send(MsgIDs.Client2ProxyReqLogin, Client2ProxyReqLogin.encode(loginReq).finish())
  })

  if (!wsClient['isConnected']) {
    wsClient.connect()
  } else {
    const actReq = Client2ProxyReqActivityDetail.create({ UserID: 0 })
    wsClient.send(
      MsgIDs.Client2ProxyReqActivityDetail,
      Client2ProxyReqActivityDetail.encode(actReq).finish(),
    )
  }
})

onUnmounted(() => {
  wsClient.off(MsgIDs.Client2ProxyAckActivityDetail, handleActivityAck)
  wsClient.off(MsgIDs.ClientActivityDataPush, handleActivityPush)
})
</script>

<template>
  <HallTop
    v-model:activeTab="activeTab"
    @open-sidebar="showSidebar = true"
    @open-pinduo="showPinduo = true"
  >
    <SidebarMenu
      v-if="showSidebar"
      @close="showSidebar = false"
      @navigate="handleSidebarNavigate"
    />

    <!-- Pinduo Overlay (Highest Layer) -->
    <div v-if="showPinduo" class="pinduo-overlay">
      <GiftSelection v-if="!hasClaimedToday" @pick="handleGiftPick" />
      <PinDuoPanel v-else @back="showPinduo = false" />
    </div>

    <div v-if="activeTab === 'Deposit'">
      <DepositPanel />
    </div>

    <div v-else-if="activeTab === 'Account'">
      <AccountPanel @navigate="handleSidebarNavigate" />
    </div>

    <div v-else-if="activeTab === 'Refer'">
      <!-- Default Refer Page Content here if any -->
    </div>

    <div v-else>
      <!-- Carousel Banner -->
      <HallBanner />

      <!-- Quick Actions Grid -->
      <QuickActionActivities
        v-if="!showMoreActionsPopup"
        :quickActions="quickActions"
        @clickAct="handleActivityClick"
        @openMore="showMoreActionsPopup = true"
      />

      <!-- Full Actions Popup -->
      <AllActivitiesPopup
        v-if="showMoreActionsPopup"
        :quickActions="quickActions"
        @clickAct="handleActivityClick"
        @close="showMoreActionsPopup = false"
      />

      <!-- Categories -->
      <section class="category-tabs">
        <button
          class="cat-btn all"
          :class="{ active: activeCategory === GameType.All }"
          @click="activeCategory = GameType.All"
        >
          <img src="@/assets/hall/common/hall_all.webp" class="cat-icon-img" /> All
        </button>
        <button
          class="cat-btn played"
          :class="{ active: activeCategory === GameType.PLAYED }"
          @click="activeCategory = GameType.PLAYED"
        >
          <img src="@/assets/hall/common/hall_played.webp" class="cat-icon-img" /> Played
        </button>
        <button
          class="cat-btn slots"
          :class="{ active: activeCategory === GameType.Slots }"
          @click="activeCategory = GameType.Slots"
        >
          <img src="@/assets/hall/common/hall_slots.webp" class="cat-icon-img" /> Slots
        </button>
        <button
          class="cat-btn arcade"
          :class="{ active: activeCategory === GameType.Arcade }"
          @click="activeCategory = GameType.Arcade"
        >
          <img src="@/assets/hall/common/hall_arcade.webp" class="cat-icon-img" /> Arcade
        </button>
        <button
          class="cat-btn tableCard"
          :class="{ active: activeCategory === GameType.TableCard }"
          @click="activeCategory = GameType.TableCard"
        >
          <img src="@/assets/hall/common/hall_table.webp" class="cat-icon-img" /> Table
        </button>
        <button
          class="cat-btn fishing"
          :class="{ active: activeCategory === GameType.Fishing }"
          @click="activeCategory = GameType.Fishing"
        >
          <img src="@/assets/hall/common/hall_fishing.webp" class="cat-icon-img" /> Fishing
        </button>
        <button
          class="cat-btn casino"
          :class="{ active: activeCategory === GameType.Casino }"
          @click="activeCategory = GameType.Casino"
        >
          <img src="@/assets/hall/common/hall_casion.webp" class="cat-icon-img" /> Casino
        </button>
        <button
          class="cat-btn lottery"
          :class="{ active: activeCategory === GameType.Lottery }"
          @click="activeCategory = GameType.Lottery"
        >
          <img src="@/assets/hall/common/hall_lottery.webp" class="cat-icon-img" /> Lottery
        </button>
      </section>

      <!-- Search & Marquee -->
      <section class="search-and-marquee">
        <div class="search-bar">
          <input type="text" v-model="searchQuery" placeholder="Search by game name..." />
          <button @click="handleSearch" class="btn-search">🔍 Search</button>
          <button @click="clearSearchInput" class="btn-clear">🗑️</button>
        </div>

        <!-- Inline Search Results Tray -->
        <transition name="expand">
          <div v-if="showSearchResults" class="search-results-tray">
            <div class="tray-title">
              Matching Games for <span class="highlight">"{{ searchQuery }}"</span>
            </div>
            <div v-if="searchResults.length > 0">
              <div class="results-grid">
                <div
                  v-for="game in displayedResults"
                  :key="'search-' + game.id"
                  class="game-card item-small cursor-pointer"
                >
                  <img :src="game.icon || defaultGameIcon" class="game-img" :alt="game.name" />
                </div>
              </div>

              <!-- Pagination Info and Load More -->
              <div class="tray-footer">
                <div class="tray-count">
                  Current
                  <span class="red-text"
                    >{{ displayedResults.length }}/{{ searchResults.length }}</span
                  >
                  Games
                </div>
                <button
                  v-if="searchResults.length > displayedResults.length"
                  @click="handleLoadMore"
                  class="btn-load-more-tray"
                >
                  Load More
                </button>
              </div>
            </div>
            <div v-else class="no-results-inline">No matches found.</div>
          </div>
        </transition>
        <div class="marquee-box">
          <span class="speaker">📢</span>
          <div class="marquee-text-container">
            <div class="marquee-text">Guest**84 deposited Rs5000.00 🎉</div>
          </div>
        </div>
      </section>

      <div v-if="activeCategory === GameType.All">
        <!-- Categories Sections -->
        <GameCategorySection title="Hot Games" icon="🔥" :gameType="GameType.HotGame" />
        <GameCategorySection title="Slot" icon="🎰" :gameType="GameType.Slots" />
        <GameCategorySection title="Table & Card" icon="🃏" :gameType="GameType.TableCard" />
        <GameCategorySection title="Casino Live" icon="👩" :gameType="GameType.Casino" />
        <GameCategorySection title="Arcade" icon="🎮" :gameType="GameType.Arcade" />
        <GameCategorySection title="Fishing Games" icon="🐟" :gameType="GameType.Fishing" />

        <ProviderListSection @open-provider="handleOpenProvider" />
      </div>

      <div v-else>
        <SingleCategoryGameList :gameType="activeCategory" />
      </div>

      <img src="@/assets/hall/anZhuang_Btn.webp" class="app-download-tag" alt="Download App" />
    </div>
  </HallTop>

  <ProviderGameListPopup
    v-if="showProviderPopup"
    :provider="selectedProvider"
    @close="showProviderPopup = false"
  />
</template>

<style scoped>
/* Main Content */
.lobby-content {
  flex: 1;
  overflow-y: auto;
  padding-top: 56px; /* Offset fixed header height */
  padding-bottom: 80px;
}

/* Banner */

/* Categories */
.category-tabs {
  display: flex;
  gap: 8px;
  padding: 0 10px;
  overflow-x: auto;
  scrollbar-width: none;
}
.category-tabs::-webkit-scrollbar {
  display: none;
}

.cat-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  color: #ccc;
  background: var(--theme-tab-inactive);
  white-space: nowrap;
}

.cat-btn.active {
  background: var(--theme-primary);
  color: #fff;
}

.cat-icon-img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

/* Search & Marquee */
.search-and-marquee {
  padding: 15px 10px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

@media (min-width: 768px) {
  .search-and-marquee {
    flex-direction: row; /* search + marquee side by side */
    align-items: center;
    gap: 12px;
  }

  .search-bar {
    flex: 0 0 40%; /* search bar takes ~40% */
    max-width: 480px;
  }

  .marquee-box {
    flex: 1; /* marquee fills rest up to ~50% total */
    max-width: 50%;
    min-width: 0;
  }
}

.search-bar {
  display: flex;
  gap: 8px;
  background: var(--theme-tab-inactive);
  border-radius: 6px;
  padding: 4px;
}

.search-bar input {
  flex: 1;
  background: transparent;
  border: none;
  color: #fff;
  padding-left: 10px;
  outline: none;
}

.btn-search {
  background: var(--theme-primary);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-weight: bold;
}

.btn-clear {
  background: transparent;
  border: none;
  color: #888;
  padding: 0 8px;
}

.marquee-box {
  display: flex;
  align-items: center;
  background: var(--theme-lobby-bg);
  border-radius: 8px;
  padding: 8px 12px;
  gap: 10px;
}

.speaker {
  color: var(--theme-marquee-speaker);
}
.marquee-text-container {
  overflow: hidden;
  white-space: nowrap;
  flex: 1;
}
.marquee-text {
  display: inline-block;
  color: var(--theme-accent);
  font-size: 0.9rem;
  /* Simple animation for text */
  animation: scrollText 10s linear infinite;
}

@keyframes scrollText {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(-100%);
  }
}

/* Remaining content below Hot Games styles... */

.pagination-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  padding: 25px 0;
}

.game-count {
  color: #888;
  font-size: 1.1rem;
}

.highlight {
  color: #ff4d4d;
  font-weight: bold;
}

.btn-load-more {
  background: var(--theme-primary);
  color: white;
  border: none;
  padding: 10px 30px;
  border-radius: 6px;
  font-weight: bold;
  font-size: 1.1rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
}

.app-download-tag {
  position: fixed;
  right: 10px;
  bottom: 85px;
  width: 50px;
  z-index: 900;
  animation: floatApp 2s ease-in-out infinite;
}

@media (min-width: 768px) {
  .app-download-tag {
    bottom: 20px; /* no bottom nav on desktop */
  }
}

/* Inline Search Results Tray */
.search-results-tray {
  background: var(--theme-bg-card);
  border-radius: 12px;
  padding: 15px;
  margin-top: 5px;
  border: 1px solid var(--theme-border-card);
}

.tray-title {
  font-size: 0.9rem;
  color: #aaa;
  margin-bottom: 12px;
}

.results-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.item-small {
  padding-bottom: 5px;
}

.game-card {
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.game-img {
  width: 100%;
  height: auto;
  display: block;
}

.no-results-inline {
  padding: 10px 0;
  color: #666;
  font-style: italic;
}

.tray-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  gap: 12px;
}

.tray-count {
  font-size: 0.9rem;
  color: #888;
}

.red-text {
  color: #ff4d4d;
  font-weight: bold;
}

.btn-load-more-tray {
  background: var(--theme-primary);
  color: white;
  border: none;
  padding: 8px 30px;
  border-radius: 6px;
  font-weight: bold;
  font-size: 1rem;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}

.btn-load-more-tray:active {
  transform: scale(0.95);
}

/* Animation for pushing content down */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease-out;
  max-height: 500px;
  opacity: 1;
  overflow: hidden;
}
.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
}

@keyframes floatApp {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0);
  }
}

.icon {
  width: 24px;
  height: 24px;
  background: #888;
  border-radius: 50%;
}

.active .icon {
  background: #ffd700;
}

.pinduo-overlay {
  position: fixed;
  top: 56px; /* below header */
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;
  background: #1a1a1a;
  overflow-y: auto;
}

@media (min-width: 768px) {
  .pinduo-overlay {
    left: 80px; /* account for PC sidebar */
  }
}
</style>
